import './EditTablePresetModal.scss';
import cn from 'classnames';
import * as R from 'ramda';
import {
  ChangeEvent,
  Dispatch,
  Fragment,
  Ref,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Form, Modal, OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import {
  MdAdd,
  MdArrowDownward,
  MdArrowUpward,
  MdClose,
  MdContentCopy,
  MdDeleteForever,
  MdEdit,
  MdFirstPage,
  MdLastPage,
  MdMoreVert,
  MdSkipNext,
  MdSkipPrevious
} from 'react-icons/md';
import { VscGripper } from 'react-icons/vsc';
import { useDrag, useDrop } from 'react-dnd';
import * as uuid from 'uuid';
import {
  DELIVERY_COLS_AND_METRICS,
  isInvalidAnalyticsDimension
} from '@blisspointmedia/bpm-types/dist/Columns';
import * as CCM from '@blisspointmedia/bpm-types/dist/CrossChannel';
import * as CC from '@blisspointmedia/bpm-types/dist/CrossChannelPerformance';
import { resolveDecimals } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { KpiInfo } from '@blisspointmedia/bpm-types/dist/Kpis';
import * as L from '@blisspointmedia/bpm-types/dist/LinearPerformance';
import { metricDecimalMap } from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import {
  Column,
  DimensionColumn,
  LINEAR,
  MetricsTablePreset,
  SortInfo,
  STREAMING_PLUS,
  SuperHeader as PresetSuperHeader
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { GlobalOptions } from '@blisspointmedia/bpm-types/dist/MetricsPage';
import { StreamingColumnCategory } from '@blisspointmedia/bpm-types/dist/Performance';
import {
  DeleteCustomMetricParams,
  SaveCustomMetric,
  SaveCustomMetricBody,
  SaveCustomMetricResponse
} from '@blisspointmedia/bpm-types/dist/CustomMetrics';
import { CustomMetric } from '@blisspointmedia/bpm-types/dist/pgTables/CustomMetric';
import { GroupedSegment } from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import * as S from '@blisspointmedia/bpm-types/dist/StreamingPerformance';
import * as Y from '@blisspointmedia/bpm-types/dist/YoutubePerformance';
import { AdminLockToggle } from '@Components/AdminLockToggle/AdminLockToggle';
import { Button } from '@Components/Button/Button';
import { Checkbox } from '@Components/FormCheck';
import { OnBlurTextInput } from '@Components/OnBlurTextInput';
import { Appearance, Design, Intent, Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { DropdownOption } from '@Components/Dropdown/dropdown-constants';
import { IconButton } from '@Components/IconButton';
import { InfoTooltip } from '@Components/InfoTooltip';
import { InteractiveDiv } from '@Components/InteractiveEl';
import {
  ColumnMetaData,
  ColumnMetaDataMap,
  convertKPIMapToOptions,
  resolveMinIsBest,
  useSortAdder
} from '@Components/MetricsTable/metrics-table-utils';
import RenameIcon from '@Components/RenameModal/RenameIcon.svg?react';
import { RenameModal } from '@Components/RenameModal/RenameModal';
import { ToggleSwitch } from '@Components/ToggleSwitch/ToggleSwitch';
import { useSetAreYouSure, useSetError } from '@src/redux/modals';
import * as UserRedux from '@src/redux/user';
import { useAppSelector } from '@src/redux';
import { getChannelSeriesColor } from '@src/utils/colors';
import {
  DeleteColumn,
  DIMENSION_COLUMN_DND_TYPE,
  COLUMN_DND_TYPE,
  getInvalidGranularityDetails,
  HeaderInfo,
  InvalidGranularityDetails,
  makeInvalidDimMsg,
  MoveColumn
} from '@src/utils/columnMetricSystem/configUtils';
import { useExperimentFlag } from '@src/utils/experiment-context';
import { useExperimentalAnalyticsCols } from '@src/utils/hooks/useExperimentalAnalyticsCols';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { awaitJSON, SingleChannelLambdaFetch } from '@src/utils/fetch-utils';
import { SingleChannelMediaTypes } from '@src/utils/performance-utils';
import { roleMap } from '@src/Pages/UserAdmin/UserAdmin';
import { CustomMetricsModal } from '@Components/MetricsTable/CustomMetricsModal/CustomMetricsModal';

// Keep these in sync with the constants in EditTablePresetModal.scss if they change.
const CONFIG_COLUMN_WIDTH = 270 as const;
const CONFIG_COLUMN_DIVIDER_WIDTH = 10 as const;
const CONFIG_COLUMN_GUTTER = 16 as const;

const SORT_ITEM_DND_TYPE = 'SORT_ITEM_DND_TYPE' as const;

const DATE_GROUPING_OPTIONS: Record<string, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year'
};

const BASIC_COLUMN_TYPES_MAP: Record<string, readonly string[]> = {
  cross_channel: CC.BASIC_COLUMN_TYPES,
  linear: L.BASIC_COLUMN_TYPES,
  streaming: S.BASIC_COLUMN_TYPES,
  youtube: Y.BASIC_COLUMN_TYPES
};

// Optional replacement for the data column's Type dropdown: two cascading dropdowns (e.g. Metric +
// Variant) that resolve to a column's dataVarName. When omitted, the column renders a plain Type
// dropdown. Used by the pacing preset editor (PacingPresetTableDashboard) for its Metric + Variant
// picker.
export interface GroupedColumnPicker {
  primaryLabel: string;
  secondaryLabel: string;
  primaryOptions: { label: string; value: string }[];
  secondaryOptions: { label: string; value: string }[];
  // Optional per-primary secondary options: a ratio metric offers a reduced variant set, so the
  // secondary dropdown narrows to the selected primary's options. Falls back to secondaryOptions.
  secondaryOptionsForPrimary?: (primary: string) => { label: string; value: string }[];
  parse: (dataVarName: string) => { primary: string; secondary: string };
  toDataVarName: (primary: string, secondary: string) => string;
  showKpiForPrimary: (primary: string) => boolean;
  showSourceForPrimary?: (primary: string) => boolean;
  sourceOptions?: { label: string; value: string }[];
  headerLabel: (
    primary: string,
    secondary: string,
    kpiDisplayName?: string,
    sourceDisplayName?: string
  ) => string;
}

interface EditTablePresetModalProps {
  columnMetaDataMap: ColumnMetaDataMap;
  defaultPresetNames?: string[];
  dimensionColumnMetaDataMap?: ColumnMetaDataMap;
  globalOptions?: GlobalOptions;
  kpiMetaData: Record<string, KpiInfo>;
  onCustomMetricsChanged?: () => void;
  onHide?: () => void;
  prefix: SingleChannelMediaTypes | 'cross_channel' | 'pacing';
  presetChanges: MetricsTablePreset;
  saveAndApplyOnClick: (tablePresetChanges: MetricsTablePreset) => void;
  setPresetChanges: Dispatch<SetStateAction<MetricsTablePreset>>;
  analyticsIsSource?: boolean;
  groupedSegments?: Record<string, GroupedSegment[]>;
  incompatibleTableDimsFromFilter?: InvalidGranularityDetails;
  // Forwarded to each data column (see GroupedColumnPicker).
  groupedColumnPicker?: GroupedColumnPicker;
}

export const EDIT_TABLE_PRESET_MODAL_CLASSNAME = 'edit-table-preset-modal';

export const EditTablePresetModal = ({
  columnMetaDataMap,
  defaultPresetNames = [],
  dimensionColumnMetaDataMap = {},
  globalOptions = { kpi: '', isDefault: false },
  kpiMetaData,
  onCustomMetricsChanged = () => {},
  onHide = () => {},
  prefix,
  presetChanges,
  saveAndApplyOnClick,
  setPresetChanges,
  analyticsIsSource,
  groupedSegments,
  incompatibleTableDimsFromFilter = {},
  groupedColumnPicker
}: EditTablePresetModalProps) => {
  const [colorMap, setColorMap] = useState<Record<string, string | undefined>>({});
  const [hoverItem, setHoverItem] = useState<string>();
  const [newName, setNewName] = useState<string | undefined>();
  const [showCustomMetricsModal, setShowCustomMetricsModal] = useState(false);
  const [showRenameCustomMetricsModal, setShowRenameCustomMetricsModal] = useState(false);
  const [currentCustomMetric, setCurrentCustomMetric] = useState<CustomMetric>();
  const [cmColumnIndex, setCMColumnIndex] = useState<number>();
  const [customMetricToSet, setCustomMetricToSet] = useState<string>();
  const [customMetricLoading, setCustomMetricLoading] = useState(false);
  const [selectedCustomMetricActionId, setSelectedCustomMetricActionId] = useState<number>();
  const adminLock = Boolean(presetChanges?.admin_lock);
  const internalOnly = Boolean(presetChanges?.role && presetChanges.role < 2);
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);
  const setAreYouSure = useSetAreYouSure();
  const setError = useSetError();
  const shouldEnableMetricsTableSegmentGroups = Boolean(
    useExperimentFlag('enableMetricsTableSegmentGroups')
  );

  useEffect(() => {
    setColorMap(currentColorMap => {
      const newColorMap = { ...currentColorMap };
      let count = Object.keys(newColorMap).length;
      for (const cols of [presetChanges.dimensionColumns, presetChanges.dataColumns]) {
        for (const col of cols) {
          if (!newColorMap[col.id]) {
            newColorMap[col.id] = getChannelSeriesColor(count);
            count += 1;
          }
        }
      }
      return newColorMap;
    });
  }, [presetChanges]);

  const moveColumn = useCallback<MoveColumn>(
    (sourceId, destinationId) => {
      let sourceI = -1;
      let destinationI = -1;
      for (let i = 0; i < presetChanges.dataColumns.length; i += 1) {
        const col = presetChanges.dataColumns[i];
        if (col.id === sourceId) {
          sourceI = i;
        }
        if (col.id === destinationId) {
          destinationI = i;
        }
      }
      if (sourceI === -1 || destinationI === -1) return;
      const reordered = R.move(sourceI, destinationI, presetChanges.dataColumns);
      let dataIndex = 0;
      setPresetChanges(current => ({
        ...current,
        dataColumns: reordered,
        superHeaders: R.reduce(
          (headers, header) => {
            // If it's a one-column-wide super header and that column is the one we're moving, delete
            // the header
            const start = dataIndex;
            const end = dataIndex + header.span - 1;
            if (start === end && start === sourceI) {
              return headers;
            }
            const sU = start <= sourceI && end >= sourceI;
            const dU = start <= destinationI && end >= destinationI;
            const sB = start > sourceI;
            const dB = start > destinationI;
            const sA = end < sourceI;
            const dA = end < destinationI;
            if ((sU && dU) || (sB && dB) || (sA && dA)) {
              dataIndex = end + 1;
              return [...headers, header];
            }
            const newHeader = { ...header };
            if (sA) {
              newHeader.span += 1;
            }
            if (sB) {
              newHeader.span -= 1;
            }
            if (dB) {
              newHeader.span += 1;
            }
            if (dA) {
              newHeader.span -= 1;
            }
            dataIndex = end + 1;
            return [...headers, newHeader];
          },
          [] as PresetSuperHeader[],
          current.superHeaders
        )
      }));
    },
    [presetChanges, setPresetChanges]
  );

  const deleteColumn = useCallback<DeleteColumn>(
    async (index: number) => {
      try {
        await setAreYouSure({
          intent: Intent.ERROR,
          title: 'Delete Column?',
          message: 'Are you sure you want to delete this column?',
          okayText: 'Delete',
          cancelText: 'Never mind'
        });

        setPresetChanges(current => {
          const dataColumns = current.dataColumns.filter((_col, i) => i !== index);
          let defaultSorting: SortInfo[] | undefined;
          if (current.defaultSorting) {
            const { id } = current.dataColumns[index];
            const newDefaultSorting = current.defaultSorting.filter(sortCol => sortCol.id !== id);
            defaultSorting = newDefaultSorting.length ? newDefaultSorting : undefined;
          }
          let columnIndex = 0;
          const superHeaders = (current.superHeaders ?? []).map(superHeader => {
            const start = columnIndex;
            const end = columnIndex + superHeader.span - 1;
            const newSuperHeader =
              index >= start && index <= end
                ? { ...superHeader, span: superHeader.span - 1 }
                : superHeader;
            columnIndex = end + 1;
            return newSuperHeader;
          });
          return { ...current, dataColumns, defaultSorting, superHeaders };
        });
      } catch {
        // Cancelled — nothing to do.
      }
    },
    [setAreYouSure, setPresetChanges]
  );

  const addColumn = useCallback(
    (i: number) => {
      const dataVarName = Object.keys(columnMetaDataMap).find(key => columnMetaDataMap[key]);
      if (!dataVarName) return;
      let dataColumnIndex = 0;
      setPresetChanges(current => ({
        ...current,
        dataColumns: R.insert(
          i,
          { id: uuid.v4(), dataVarName, role: 10 } as Column,
          current.dataColumns
        ),
        superHeaders: (current.superHeaders ?? []).map(superHeader => {
          const start = dataColumnIndex;
          const end = dataColumnIndex + superHeader.span - 1;
          if (i >= start && i <= end) {
            return { ...superHeader, span: superHeader.span + 1 };
          }
          dataColumnIndex = end + 1;
          return superHeader;
        })
      }));
    },
    [setPresetChanges, columnMetaDataMap]
  );

  const onCustomMetricsModalHide = useCallback(() => {
    setShowCustomMetricsModal(false);
    setShowRenameCustomMetricsModal(false);
    setCurrentCustomMetric(undefined);
  }, []);

  const deleteCustomMetric = useCallback(
    (customMetric: CustomMetric) => {
      (async () => {
        try {
          setCustomMetricLoading(true);
          const res = await SingleChannelLambdaFetch<DeleteCustomMetricParams>(
            '/deleteCustomMetric',
            { method: 'DELETE', params: { id: customMetric.id } }
          );
          await awaitJSON<SaveCustomMetricResponse>(res);
          onCustomMetricsChanged();
          setError({
            title: 'Success',
            intent: Intent.SUCCESS,
            message: `Deleted custom metric: ${customMetric.display_name}!`
          });
        } catch (e) {
          setError({
            message: `Failed to delete custom metric. Error: ${e.message}`,
            reportError: e
          });
        } finally {
          setCustomMetricLoading(false);
        }
      })();
    },
    [onCustomMetricsChanged, setError]
  );

  const saveCustomMetric = useCallback(
    (customMetric: Partial<CustomMetric>) => {
      (async () => {
        try {
          setCustomMetricLoading(true);
          // `SaveCustomMetric` requires audit fields (`created_by`/`created`/`last_user`/
          // `last_modified`) that the lambda actually derives server-side (auth context,
          // `now()`) rather than from the request body — the client-built object is
          // intentionally missing them.
          const res = await SingleChannelLambdaFetch<SaveCustomMetricBody>('/saveCustomMetric', {
            method: 'POST',
            body: { customMetric: customMetric as SaveCustomMetric }
          });
          const result = await awaitJSON<SaveCustomMetricResponse>(res);
          onCustomMetricsChanged();
          setError({
            title: 'Success',
            intent: Intent.SUCCESS,
            message: `Saved custom metric: ${result.display_name}!`
          });
          if (customMetric.id == null && cmColumnIndex != null) {
            setCustomMetricToSet(customMetric.name);
          }
        } catch (e) {
          setError({
            message: `Failed to save custom metric. Error: ${e.message}`,
            reportError: e
          });
        } finally {
          setCustomMetricLoading(false);
          onCustomMetricsModalHide();
        }
      })();
    },
    [cmColumnIndex, onCustomMetricsChanged, onCustomMetricsModalHide, setError]
  );

  // Once a newly-saved custom metric's ColumnMetaData entry lands in `columnMetaDataMap`
  // (after `onCustomMetricsChanged` triggers a refetch upstream), auto-select it on the
  // column that opened the "Add New Custom Metric" flow.
  useEffect(() => {
    if (cmColumnIndex == null || !customMetricToSet) return;
    const meta = columnMetaDataMap[customMetricToSet];
    if (!meta) return;
    setPresetChanges(changes => ({
      ...changes,
      dataColumns: R.adjust(
        cmColumnIndex,
        column => ({
          ...column,
          dataVarName: customMetricToSet,
          requiredColumns: meta.requiredColumns ? [...meta.requiredColumns] : undefined,
          customColumnID: meta.customMetaData?.id
        }),
        changes.dataColumns
      )
    }));
    setCMColumnIndex(undefined);
    setCustomMetricToSet(undefined);
  }, [cmColumnIndex, columnMetaDataMap, customMetricToSet, setPresetChanges]);

  const superHeaderInfoMap = useMemo(() => {
    const map: Record<string, HeaderInfo | true> = {};
    let columnIndex = 0;
    if (presetChanges.superHeaders) {
      for (let superHeaderIndex = 0; superHeaderIndex < presetChanges.superHeaders.length;) {
        const superHeader = presetChanges.superHeaders[superHeaderIndex];
        const start = columnIndex;
        const end = columnIndex + superHeader.span - 1;
        if (superHeader.data.text) {
          // Take gutter out so half gutter on either side is removed
          let width = -CONFIG_COLUMN_GUTTER;
          for (let i = start; i <= end; i += 1) {
            map[i] = true;
            width += CONFIG_COLUMN_WIDTH + CONFIG_COLUMN_GUTTER;
            if (presetChanges.dataColumns[i]?.divider) {
              width += CONFIG_COLUMN_DIVIDER_WIDTH;
            }
          }
          map[start] = {
            end,
            start,
            superHeaderIndex,
            text: superHeader.data.text,
            width
          };
        }
        columnIndex = end + 1;
        superHeaderIndex += 1;
      }
    }
    return map;
  }, [presetChanges]);

  const onSuperHeaderChange = useCallback(
    (newSuperHeaders: PresetSuperHeader[]) => {
      // Clean up the super headers to collapse blanks
      const cleanedSuperHeaders = newSuperHeaders.reduce<PresetSuperHeader[]>((acc, current) => {
        if (current.data.text === '') {
          if (acc.length === 0 || acc[acc.length - 1].data.text !== '') {
            return [...acc, current];
          }
          const last = acc[acc.length - 1];
          return [...acc.slice(0, -1), { ...last, span: last.span + current.span }];
        }
        return [...acc, current];
      }, []);
      setPresetChanges(current => ({ ...current, superHeaders: cleanedSuperHeaders }));
    },
    [setPresetChanges]
  );

  const deleteDimensionColumn = useCallback<DeleteColumn>(
    async (index: number) => {
      try {
        await setAreYouSure({
          intent: Intent.ERROR,
          title: 'Delete Column?',
          message: 'Are you sure you want to delete this column?',
          okayText: 'Delete',
          cancelText: 'Never mind'
        });
        setPresetChanges(current => {
          const dimensionColumns = current.dimensionColumns.filter((_col, i) => {
            if (i === index) return false;
            if (
              groupedSegments &&
              shouldEnableMetricsTableSegmentGroups &&
              Object.keys(groupedSegments).includes(
                current.dimensionColumns[index].dimensionTypeName
              )
            ) {
              return !groupedSegments[current.dimensionColumns[index].dimensionTypeName]?.some(
                segment => segment.segmentName === current.dimensionColumns[i].dimensionVarName
              );
            }
            return true;
          });
          let defaultSorting: SortInfo[] | undefined;
          if (current.defaultSorting) {
            const { id } = current.dimensionColumns[index];
            const newDefaultSorting = current.defaultSorting.filter(sortCol => sortCol.id !== id);
            defaultSorting = newDefaultSorting.length ? newDefaultSorting : undefined;
          }
          return { ...current, dimensionColumns, defaultSorting };
        });
      } catch {
        // Cancelled — nothing to do.
      }
    },
    [setAreYouSure, setPresetChanges, groupedSegments, shouldEnableMetricsTableSegmentGroups]
  );

  const moveDimensionColumn = useCallback<MoveColumn>(
    (sourceId, destinationId) => {
      let sourceI = -1;
      let destinationI = -1;
      for (let i = 0; i < presetChanges.dimensionColumns.length; i += 1) {
        const col = presetChanges.dimensionColumns[i];
        if (col.id === sourceId) {
          sourceI = i;
        }
        if (col.id === destinationId) {
          destinationI = i;
        }
      }
      if (sourceI === -1 || destinationI === -1) return;
      const reordered = R.move(sourceI, destinationI, presetChanges.dimensionColumns);
      setPresetChanges(current => ({ ...current, dimensionColumns: reordered }));
    },
    [presetChanges, setPresetChanges]
  );

  const addDimensionColumn = useCallback(
    (i: number) => {
      const dimensionTypeName = Object.keys(dimensionColumnMetaDataMap).find(
        key => dimensionColumnMetaDataMap[key]
      );
      const dimensionVarName = dimensionTypeName
        ? (dimensionColumnMetaDataMap[dimensionTypeName] as ColumnMetaData).name
        : undefined;
      if (!dimensionTypeName || !dimensionVarName) return;
      setPresetChanges(current => ({
        ...current,
        dimensionColumns: R.insert(
          i,
          { dimensionTypeName, dimensionVarName, id: uuid.v4(), role: 10 } as DimensionColumn,
          current.dimensionColumns
        )
      }));
    },
    [dimensionColumnMetaDataMap, setPresetChanges]
  );

  const addSuperHeader = useCallback(
    (columnI: number) => {
      const newSuperHeaderData = {
        text: 'New Super Header',
        leftDivider: false,
        rightDivider: false
      };
      const superHeaders: PresetSuperHeader[] = [];
      let dataColumnIndex = 0;
      let inserted = false;
      for (const superHeader of presetChanges.superHeaders ?? []) {
        const start = dataColumnIndex;
        const end = dataColumnIndex + superHeader.span - 1;
        if (start > columnI || end < columnI) {
          superHeaders.push(superHeader);
        } else if (start === end && start === columnI) {
          superHeaders.push({ ...superHeader, data: newSuperHeaderData });
          inserted = true;
        } else if (start === columnI) {
          superHeaders.push({ span: 1, data: newSuperHeaderData });
          superHeaders.push({ ...superHeader, span: superHeader.span - 1 });
          inserted = true;
        } else if (end === columnI) {
          superHeaders.push({ ...superHeader, span: superHeader.span - 1 });
          superHeaders.push({ span: 1, data: newSuperHeaderData });
          inserted = true;
        } else {
          const leftSpan = columnI - start;
          superHeaders.push({ ...superHeader, span: leftSpan });
          superHeaders.push({ span: 1, data: newSuperHeaderData });
          superHeaders.push({ ...superHeader, span: superHeader.span - leftSpan - 1 });
          inserted = true;
        }
        dataColumnIndex = end + 1;
      }
      if (!inserted) {
        if (columnI > dataColumnIndex) {
          superHeaders.push({
            data: { leftDivider: false, rightDivider: false, text: '' },
            span: columnI - dataColumnIndex
          });
        }
        superHeaders.push({ data: newSuperHeaderData, span: 1 });
      }
      onSuperHeaderChange(superHeaders);
    },
    [onSuperHeaderChange, presetChanges.superHeaders]
  );

  const hasSuperHeaderMap = useMemo(() => {
    const hasSuperHeaderList: boolean[] = [];
    let colIndex = 0;
    for (const superHeader of presetChanges.superHeaders ?? []) {
      for (let i = 0; i < superHeader.span; i += 1) {
        if (superHeader.data.text) {
          hasSuperHeaderList[colIndex] = true;
        }
        colIndex += 1;
      }
    }
    return hasSuperHeaderList;
  }, [presetChanges]);

  const modalHeader = (
    <Modal.Header>
      <div className='title-container'>
        <Modal.Title>Table Preset:</Modal.Title>
        <div className='table-preset-name-container'>
          {newName !== undefined ? (
            <Form.Control
              className='new-name-input'
              onChange={(e: ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
              placeholder='New Preset Name'
              value={newName}
            />
          ) : (
            <div className='table-preset-name'>
              {presetChanges.temporary ? 'Temporary Preset' : presetChanges.name}
            </div>
          )}
          {presetChanges?.name &&
            !defaultPresetNames.includes(presetChanges.name.toLowerCase()) &&
            (newName === undefined ? (
              <IconButton
                size={Size.MD}
                appearance={Appearance.EMPTY}
                className='edit-table-preset-name-icon'
                onClick={() => setNewName(presetChanges.name)}
              >
                <MdEdit />
              </IconButton>
            ) : (
              <IconButton
                size={Size.MD}
                appearance={Appearance.EMPTY}
                className='cancel-rename-icon'
                onClick={() => setNewName(undefined)}
              >
                <MdClose />
              </IconButton>
            ))}
        </div>
      </div>
      <AdminLockToggle
        checked={adminLock}
        onChange={() => setPresetChanges(current => ({ ...current, admin_lock: !adminLock }))}
        className='internal-only-toggle'
        disabled={!isAdmin}
      />
      <ToggleSwitch
        checked={internalOnly}
        className='internal-only-toggle'
        design={Design.BRAND}
        label='Internal-only'
        onChange={() => setPresetChanges(current => ({ ...current, role: internalOnly ? 10 : 1 }))}
      />
      <IconButton
        appearance={Appearance.EMPTY}
        className='close-button'
        onClick={() => onHide()}
        size={Size.MD}
      >
        <MdClose />
      </IconButton>
    </Modal.Header>
  );

  const modalBody = Object.keys(columnMetaDataMap).length > 0 && (
    <Modal.Body>
      <div className='config-box'>
        <SortOrderBox
          colorMap={colorMap}
          presetChanges={presetChanges}
          setHoverItem={setHoverItem}
          setPresetChanges={setPresetChanges}
        />
        <div
          className={cn('config-box-inner', {
            'has-headers':
              presetChanges.superHeaders &&
              ((presetChanges.superHeaders[0] && presetChanges.superHeaders[0].data.text) ||
                presetChanges.superHeaders.length > 1)
          })}
        >
          {presetChanges.dimensionColumns
            .filter(
              column =>
                !(
                  groupedSegments &&
                  shouldEnableMetricsTableSegmentGroups &&
                  Object.values(groupedSegments)
                    .flatMap(group => Object.values(group))
                    .some(segment => segment.segmentName === column.dimensionTypeName)
                )
            )
            .map((column, i) => (
              <ConfigDimensionColumnItem
                key={column.id}
                addColumn={addDimensionColumn}
                color={colorMap[column.id]}
                column={column}
                columnMetaDataMap={dimensionColumnMetaDataMap}
                deleteColumn={deleteDimensionColumn}
                dimensionColumns={presetChanges.dimensionColumns}
                groupedSegments={groupedSegments}
                hoverItem={hoverItem}
                index={i}
                isOnlyColumn={presetChanges.dimensionColumns.length <= 1}
                moveColumn={moveDimensionColumn}
                analyticsIsSource={analyticsIsSource}
                prefix={prefix}
                setPresetChanges={setPresetChanges}
                invalidGranularityDetails={getInvalidGranularityDetails(
                  [
                    ...presetChanges.dimensionColumns
                      .filter(col => col.id !== column.id)
                      .map(info => info.dimensionVarName),
                    ...presetChanges.dataColumns.map(col => col.dataVarName)
                  ],
                  Object.values(dimensionColumnMetaDataMap).map(col => col?.name ?? ''),
                  prefix
                )}
                incompatibleTableDimsFromFilter={incompatibleTableDimsFromFilter}
              />
            ))}
          <div className='column-divider' />
          {presetChanges.dataColumns.map((column, i) => (
            <Fragment key={column.id}>
              <SuperHeaderEditor
                colCount={presetChanges.dataColumns.length}
                columnI={i}
                info={superHeaderInfoMap}
                onSuperHeaderChange={onSuperHeaderChange}
                superHeaders={presetChanges.superHeaders ?? []}
              />
              <ConfigDataColumn
                addColumn={addColumn}
                addCustomMetric={() => {
                  setShowCustomMetricsModal(true);
                  setCMColumnIndex(i);
                }}
                color={colorMap[column.id]}
                column={column}
                columnMetaDataMap={columnMetaDataMap}
                deleteColumn={deleteColumn}
                deleteCustomMetric={deleteCustomMetric}
                duplicateCustomMetric={(customMetric: SaveCustomMetric) =>
                  saveCustomMetric({
                    ...customMetric,
                    display_name: `${customMetric.display_name} (Copy)`,
                    name: `${customMetric.name}_copy`,
                    id: undefined
                  })
                }
                editCustomMetric={customMetric => {
                  setShowCustomMetricsModal(true);
                  setCurrentCustomMetric(customMetric);
                }}
                globalKpi={globalOptions.kpi}
                groupedColumnPicker={groupedColumnPicker}
                hasSuperHeader={hasSuperHeaderMap[i]}
                hoverItem={hoverItem}
                index={i}
                isOnlyColumn={presetChanges.dataColumns.length <= 1}
                kpiMetaData={kpiMetaData}
                selectedCustomMetricActionId={selectedCustomMetricActionId}
                setSelectedCustomMetricActionId={setSelectedCustomMetricActionId}
                moveColumn={moveColumn}
                onSuperHeaderAdd={() => addSuperHeader(i)}
                prefix={prefix}
                renameCustomMetric={customMetric => {
                  setCurrentCustomMetric(customMetric);
                  setShowRenameCustomMetricsModal(true);
                }}
                setPresetChanges={setPresetChanges}
                invalidGranularityDetails={getInvalidGranularityDetails(
                  [
                    ...presetChanges.dimensionColumns.map(info => info.dimensionVarName),
                    ...presetChanges.dataColumns
                      .filter(col => col.id !== column.id)
                      .map(col => col.dataVarName)
                  ],
                  Object.keys(columnMetaDataMap),
                  prefix
                )}
                incompatibleTableDataColsFromFilter={incompatibleTableDimsFromFilter}
              />
            </Fragment>
          ))}
        </div>
      </div>
    </Modal.Body>
  );

  const modalFooter = (
    <Modal.Footer>
      <Button
        className='cancel-button'
        appearance={Appearance.OUTLINED}
        design={Design.NEUTRAL}
        onClick={() => onHide()}
      >
        Cancel
      </Button>
      <Button
        className='preview-button'
        appearance={Appearance.OUTLINED}
        design={Design.NEUTRAL}
        onClick={() =>
          saveAndApplyOnClick({
            ...presetChanges,
            temporary: true,
            id: presetChanges.temporary ? presetChanges.id : undefined
          } as MetricsTablePreset)
        }
      >
        Temp Preview
      </Button>
      <Button
        className='save-and-apply-button'
        disabled={adminLock && !isAdmin}
        onClick={() => {
          // Save and Apply always persists a permanent preset — saving a temp view converts it.
          const newPreset = {
            ...presetChanges,
            name: newName ?? presetChanges.name,
            temporary: false
          };
          saveAndApplyOnClick(newPreset);
          setPresetChanges(newPreset);
        }}
      >
        Save and Apply
      </Button>
    </Modal.Footer>
  );

  return (
    <>
      <Modal
        show
        centered
        size='lg'
        onHide={() => onHide()}
        className={EDIT_TABLE_PRESET_MODAL_CLASSNAME}
      >
        {modalHeader}
        {modalBody}
        {modalFooter}
      </Modal>
      {showCustomMetricsModal && !customMetricLoading && (
        <CustomMetricsModal
          columnMetaDataMap={columnMetaDataMap}
          customMetric={currentCustomMetric}
          disabled={customMetricLoading}
          kpiMetaData={kpiMetaData}
          onHide={onCustomMetricsModalHide}
          prefix={prefix}
          save={saveCustomMetric}
        />
      )}
      {showRenameCustomMetricsModal && !customMetricLoading && (
        <RenameModal
          applyOnClick={newDisplayName => {
            if (!currentCustomMetric) return;
            // A rename changes `display_name` only. `name` is the metric's permanent id (the
            // `dataVarName` key in metrics_table_presets), so it is deliberately NOT re-derived --
            // the backend ignores it on update, and re-deriving it here is what used to orphan
            // presets. It still rides along in the spread, unchanged, which is correct.
            saveCustomMetric({
              ...currentCustomMetric,
              display_name: newDisplayName
            });
          }}
          applyText='Save'
          cancelText='Cancel'
          disabled={customMetricLoading}
          onCancel={onCustomMetricsModalHide}
          onHide={onCustomMetricsModalHide}
          placeholder='Rename custom metric'
          subTitle={`Rename ${currentCustomMetric?.display_name}`}
          title='Rename Custom Metric'
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Private sub-components below — none of these are reused outside this modal.
// ---------------------------------------------------------------------------

interface CustomMetricActionHandlers {
  deleteCustomMetric: (customMetric: CustomMetric) => void;
  duplicateCustomMetric: (customMetric: CustomMetric) => void;
  editCustomMetric: (customMetric: CustomMetric) => void;
  renameCustomMetric: (customMetric: CustomMetric) => void;
}

// Three-dot overflow menu for a custom-metric row in the Type dropdown (`itemOptions`) —
// a row of always-visible icon buttons read as cluttered next to the plain "custom" badge.
// Same controlled-`show` + explicit-close-on-select pattern as PresetDropdown's "..." menu
// (@Components/PresetDropdown/PresetDropdown.tsx) — react-bootstrap's OverlayTrigger only
// closes on outside-click/escape by default, so an item click inside it never closes the
// menu unless `show` is driven by state and each action clears it before running.
const renderCustomMetricActions = (
  customMetric: CustomMetric,
  selectedCustomMetricActionId: number | undefined,
  setSelectedCustomMetricActionId: (id: number | undefined) => void,
  {
    deleteCustomMetric,
    duplicateCustomMetric,
    editCustomMetric,
    renameCustomMetric
  }: CustomMetricActionHandlers
) => {
  const runAndClose = (action: (customMetric: CustomMetric) => void) => () => {
    setSelectedCustomMetricActionId(undefined);
    action(customMetric);
  };
  return (
    <OverlayTrigger
      show={selectedCustomMetricActionId === customMetric.id}
      placement={Placement.LEFT_START}
      overlay={
        <Popover id={`custom-metric-${customMetric.id}-actions-popover`}>
          <div className='custom-metric-actions'>
            <InteractiveDiv
              className='custom-metric-action'
              data-testid={`custom-metric-edit-${customMetric.id}`}
              onClick={runAndClose(editCustomMetric)}
            >
              <div className='custom-metric-action-icon'>
                <MdEdit />
              </div>
              <div className='custom-metric-action-text'>Edit</div>
            </InteractiveDiv>
            <InteractiveDiv
              className='custom-metric-action'
              data-testid={`custom-metric-rename-${customMetric.id}`}
              onClick={runAndClose(renameCustomMetric)}
            >
              <div className='custom-metric-action-icon'>
                <RenameIcon />
              </div>
              <div className='custom-metric-action-text'>Rename</div>
            </InteractiveDiv>
            <InteractiveDiv
              className='custom-metric-action'
              data-testid={`custom-metric-duplicate-${customMetric.id}`}
              onClick={runAndClose(duplicateCustomMetric)}
            >
              <div className='custom-metric-action-icon'>
                <MdContentCopy />
              </div>
              <div className='custom-metric-action-text'>Duplicate</div>
            </InteractiveDiv>
            <InteractiveDiv
              className='custom-metric-action'
              data-testid={`custom-metric-delete-${customMetric.id}`}
              onClick={runAndClose(deleteCustomMetric)}
            >
              <div className='custom-metric-action-icon'>
                <MdDeleteForever />
              </div>
              <div className='custom-metric-action-text'>Delete</div>
            </InteractiveDiv>
          </div>
        </Popover>
      }
    >
      <IconButton
        className='custom-metric-actions-expander'
        appearance={Appearance.EMPTY}
        data-testid={`custom-metric-actions-${customMetric.id}`}
        onClick={() =>
          setSelectedCustomMetricActionId(
            selectedCustomMetricActionId === customMetric.id ? undefined : customMetric.id
          )
        }
        tooltip={{ text: 'More actions' }}
      >
        <MdMoreVert />
      </IconButton>
    </OverlayTrigger>
  );
};

interface ColumnHeaderTopProps {
  noHeaders?: boolean;
  hasSuperHeader?: boolean;
  onSuperHeaderAdd?: () => void;
  handleRef: Ref<HTMLDivElement>;
  index: number;
  addColumn: (i: number) => void;
  color?: string;
}

const ColumnHeaderTop = ({
  color,
  onSuperHeaderAdd,
  hasSuperHeader,
  handleRef,
  index,
  addColumn,
  noHeaders
}: ColumnHeaderTopProps) => (
  <>
    <div className='stripe' style={{ backgroundColor: color || 'black' }} />
    <div className='top-actions'>
      <OverlayTrigger placement={Placement.TOP_START} overlay={<Tooltip>Drag and drop</Tooltip>}>
        <div className='drag-handle' ref={handleRef}>
          <VscGripper />
        </div>
      </OverlayTrigger>
      <div className='add-actions'>
        <div className='add-label'>Add:</div>
        <OverlayTrigger
          placement={Placement.TOP_END}
          overlay={<Tooltip>Column to the left</Tooltip>}
        >
          <IconButton
            appearance={Appearance.OUTLINED}
            onClick={() => addColumn(index)}
            size={Size.MD}
          >
            <MdFirstPage />
          </IconButton>
        </OverlayTrigger>
        {!noHeaders && (
          <OverlayTrigger
            placement={Placement.TOP}
            overlay={
              <Tooltip>{hasSuperHeader ? 'There is already a header' : 'Header above'}</Tooltip>
            }
          >
            <IconButton
              size={Size.MD}
              appearance={Appearance.OUTLINED}
              className='add-header-button'
              disabled={hasSuperHeader}
              onClick={() => onSuperHeaderAdd?.()}
            >
              <MdFirstPage className='add-header-icon' />
            </IconButton>
          </OverlayTrigger>
        )}
        <OverlayTrigger
          placement={Placement.TOP_START}
          overlay={<Tooltip>Column to the right</Tooltip>}
        >
          <IconButton
            appearance={Appearance.OUTLINED}
            onClick={() => addColumn(index + 1)}
            size={Size.MD}
          >
            <MdLastPage />
          </IconButton>
        </OverlayTrigger>
      </div>
    </div>
  </>
);

interface SuperHeaderEditorProps {
  colCount: number;
  columnI: number;
  info: Record<string, HeaderInfo | true | undefined>;
  onSuperHeaderChange: (headers: PresetSuperHeader[]) => void;
  superHeaders: PresetSuperHeader[];
}

const SuperHeaderEditor = ({
  colCount,
  columnI,
  info,
  onSuperHeaderChange,
  superHeaders
}: SuperHeaderEditorProps) => {
  const ourInfo = info[columnI];
  const ourHeaderInfo = ourInfo && typeof ourInfo !== 'boolean' ? ourInfo : undefined;

  const onUpdateText = useCallback(
    (text: string) => {
      if (!ourHeaderInfo) return;
      const newSuperHeaders = [...superHeaders];
      newSuperHeaders[ourHeaderInfo.superHeaderIndex] = {
        ...newSuperHeaders[ourHeaderInfo.superHeaderIndex],
        data: { ...newSuperHeaders[ourHeaderInfo.superHeaderIndex].data, text }
      };
      onSuperHeaderChange(newSuperHeaders);
    },
    [onSuperHeaderChange, ourHeaderInfo, superHeaders]
  );

  const onDelete = useCallback(() => onUpdateText(''), [onUpdateText]);

  const onResize = useCallback(
    (grow: boolean, side: 'end' | 'start') => {
      if (!ourHeaderInfo) return;
      const newSuperHeaders = [...superHeaders];
      const { superHeaderIndex } = ourHeaderInfo;
      newSuperHeaders[superHeaderIndex] = {
        ...newSuperHeaders[superHeaderIndex],
        span: newSuperHeaders[superHeaderIndex].span + (grow ? 1 : -1)
      };
      if (side === 'start' && newSuperHeaders[superHeaderIndex - 1]) {
        newSuperHeaders[superHeaderIndex - 1] = {
          ...newSuperHeaders[superHeaderIndex - 1],
          span: newSuperHeaders[superHeaderIndex - 1].span + (grow ? -1 : 1)
        };
      } else if (side === 'end' && newSuperHeaders[superHeaderIndex + 1]) {
        newSuperHeaders[superHeaderIndex + 1] = {
          ...newSuperHeaders[superHeaderIndex + 1],
          span: newSuperHeaders[superHeaderIndex + 1].span + (grow ? -1 : 1)
        };
      }
      onSuperHeaderChange(newSuperHeaders);
    },
    [onSuperHeaderChange, ourHeaderInfo, superHeaders]
  );

  const { canLeft, canRight, size } = useMemo(() => {
    if (!ourHeaderInfo) return { canLeft: false, canRight: false, size: 0 };
    const lastSuperHeader = superHeaders[ourHeaderInfo.superHeaderIndex - 1];
    const superHeader = superHeaders[ourHeaderInfo.superHeaderIndex];
    const nextSuperHeader = superHeaders[ourHeaderInfo.superHeaderIndex + 1];
    return {
      canLeft: ourHeaderInfo.start > 0 && !lastSuperHeader?.data.text,
      canRight: ourHeaderInfo.end < colCount - 1 && !nextSuperHeader?.data.text,
      size: superHeader.span
    };
  }, [ourHeaderInfo, superHeaders, colCount]);

  const [localText, setLocalText] = useState(ourHeaderInfo?.text ?? '');

  useEffect(() => {
    setLocalText(ourHeaderInfo?.text ?? '');
  }, [ourHeaderInfo]);

  if (!ourHeaderInfo) return null;

  return (
    <div className='super-header-container'>
      <div style={{ width: `${ourHeaderInfo.width}px` }}>
        <div className='sizer-box'>
          {canLeft && (
            <IconButton
              appearance={Appearance.OUTLINED}
              onClick={() => onResize(true, 'start')}
              size={Size.MD}
            >
              <MdSkipPrevious />
            </IconButton>
          )}
          {size > 1 && (
            <IconButton
              appearance={Appearance.OUTLINED}
              onClick={() => onResize(false, 'start')}
              size={Size.MD}
            >
              <MdSkipNext />
            </IconButton>
          )}
        </div>
        <Form.Control
          onBlur={() => onUpdateText(localText)}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setLocalText(e.currentTarget.value)}
          size='sm'
          value={localText}
        />
        <IconButton
          appearance={Appearance.OUTLINED}
          design={Intent.ERROR}
          onClick={onDelete}
          size={Size.MD}
        >
          <MdDeleteForever />
        </IconButton>
        <div className='sizer-box'>
          {size > 1 && (
            <IconButton
              appearance={Appearance.OUTLINED}
              onClick={() => onResize(false, 'end')}
              size={Size.MD}
            >
              <MdSkipPrevious />
            </IconButton>
          )}
          {canRight && (
            <IconButton
              appearance={Appearance.OUTLINED}
              onClick={() => onResize(true, 'end')}
              size={Size.MD}
            >
              <MdSkipNext />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
};

interface SortOrderBoxProps {
  colorMap: Record<string, string | undefined>;
  presetChanges: MetricsTablePreset;
  setHoverItem: Dispatch<SetStateAction<string | undefined>>;
  setPresetChanges: Dispatch<SetStateAction<MetricsTablePreset>>;
}

const SortOrderBox = ({
  presetChanges,
  setPresetChanges,
  setHoverItem,
  colorMap
}: SortOrderBoxProps) => {
  const newSortInfo = presetChanges.defaultSorting ?? [];
  const setNewSortInfo = useCallback(
    (func: SortInfo[] | ((arr: SortInfo[]) => SortInfo[])) => {
      setPresetChanges(changes => ({
        ...changes,
        defaultSorting: typeof func === 'function' ? func(changes.defaultSorting ?? []) : func
      }));
    },
    [setPresetChanges]
  );
  const addSorting = useSortAdder(setNewSortInfo);
  const [{ isOver }, drop] = useDrop<
    { type: typeof COLUMN_DND_TYPE | typeof DIMENSION_COLUMN_DND_TYPE; id: string },
    void,
    { isOver: boolean }
  >({
    accept: [COLUMN_DND_TYPE, DIMENSION_COLUMN_DND_TYPE],
    canDrop: item => !newSortInfo.some(existing => existing.id === item.id),
    collect: monitor => ({
      isOver: monitor.isOver() && monitor.canDrop()
    }),
    drop: item => addSorting(item.id)
  });
  const dropRef = useCallback(
    (node: HTMLDivElement | null) => {
      drop(node);
    },
    [drop]
  );
  return (
    <div className={cn('sort-order-box', { 'is-over': isOver })} ref={dropRef}>
      <div>Sort Order</div>
      {newSortInfo.length === 0 && <div className='drag-here'>Drag and drop here</div>}
      <div className='sort-item-box'>
        {newSortInfo.map(item => (
          <SortOrderItem
            colorMap={colorMap}
            item={item}
            key={item.id}
            onClick={() => addSorting(item.id)}
            setHoverItem={setHoverItem}
            setNewSortInfo={setNewSortInfo}
          />
        ))}
      </div>
    </div>
  );
};

interface SortOrderItemProps {
  item: SortInfo;
  colorMap: Record<string, string | undefined>;
  setHoverItem: Dispatch<SetStateAction<string | undefined>>;
  setNewSortInfo: (func: (arr: SortInfo[]) => SortInfo[]) => void;
  onClick: () => void;
}

const SortOrderItem = ({
  item,
  colorMap,
  setHoverItem,
  setNewSortInfo,
  onClick
}: SortOrderItemProps) => {
  const { id, asc } = item;

  const [, drag, preview] = useDrag<SortInfo & { type: typeof SORT_ITEM_DND_TYPE }>({
    type: SORT_ITEM_DND_TYPE,
    item: { type: SORT_ITEM_DND_TYPE, id, asc }
  });

  const [{ isOver }, drop] = useDrop<
    SortInfo & { type: typeof SORT_ITEM_DND_TYPE },
    void,
    { isOver: boolean }
  >({
    accept: SORT_ITEM_DND_TYPE,
    canDrop: dragged => dragged.id !== id,
    collect: monitor => ({
      isOver: monitor.isOver() && monitor.canDrop()
    }),
    drop: draggedItem => {
      setNewSortInfo(existing =>
        existing.reduce<SortInfo[]>((agg, elem) => {
          if (elem.id === id) {
            return [...agg, R.omit(['type'], draggedItem), elem];
          }
          if (elem.id === draggedItem.id) {
            return agg;
          }
          return [...agg, elem];
        }, [])
      );
    }
  });

  const dragDropRef = useCallback(
    (node: HTMLDivElement | null) => {
      preview(node);
      drag(node);
      drop(node);
    },
    [preview, drag, drop]
  );

  return (
    <InteractiveDiv
      ref={dragDropRef}
      style={{ borderColor: colorMap[id] || 'black' }}
      className={cn('sort-order-item', { 'is-over': isOver })}
      onMouseOver={() => setHoverItem(id)}
      onFocus={() => setHoverItem(id)}
      onMouseLeave={() => setHoverItem(current => (current === id ? undefined : current))}
      onClick={() => {
        if (!asc) {
          setHoverItem(undefined);
        }
        onClick();
      }}
    >
      {asc ? <MdArrowUpward /> : <MdArrowDownward />}
    </InteractiveDiv>
  );
};

interface ConfigDimensionColumnProps {
  addColumn: (i: number) => void;
  color?: string;
  column: DimensionColumn;
  columnMetaDataMap: ColumnMetaDataMap;
  deleteColumn: DeleteColumn;
  dimensionColumns: DimensionColumn[];
  groupedSegments?: Record<string, GroupedSegment[]>;
  hoverItem?: string;
  index: number;
  isOnlyColumn: boolean;
  moveColumn: MoveColumn;
  analyticsIsSource?: boolean;
  prefix: SingleChannelMediaTypes | 'cross_channel' | 'pacing';
  setPresetChanges: Dispatch<SetStateAction<MetricsTablePreset>>;
  invalidGranularityDetails?: InvalidGranularityDetails;
  incompatibleTableDimsFromFilter?: InvalidGranularityDetails;
}

const ConfigDimensionColumnItem = ({
  addColumn,
  color,
  column,
  columnMetaDataMap = {},
  deleteColumn,
  dimensionColumns,
  groupedSegments,
  hoverItem,
  index,
  isOnlyColumn,
  moveColumn,
  analyticsIsSource = false,
  prefix,
  setPresetChanges,
  invalidGranularityDetails = {},
  incompatibleTableDimsFromFilter = {}
}: ConfigDimensionColumnProps) => {
  const { dimensionTypeName, icon, id, label, role, dateGrouping } = column;
  const shouldEnableMetricsTableSegmentGroups = Boolean(
    useExperimentFlag('enableMetricsTableSegmentGroups')
  );
  const expAnalyticsCols = useExperimentalAnalyticsCols();

  const [{ isDragging }, drag, preview] = useDrag<
    { type: typeof DIMENSION_COLUMN_DND_TYPE; id: string; index: number },
    void,
    { isDragging: boolean }
  >({
    type: DIMENSION_COLUMN_DND_TYPE,
    item: { type: DIMENSION_COLUMN_DND_TYPE, id, index },
    collect: monitor => ({ isDragging: monitor.isDragging() })
  });

  const [{ isOver, dropIndex }, drop] = useDrop<
    { type: typeof DIMENSION_COLUMN_DND_TYPE; id: string; index: number },
    void,
    { isOver: boolean; dropIndex: number | undefined }
  >({
    accept: DIMENSION_COLUMN_DND_TYPE,
    canDrop: item => item.id !== id,
    collect: monitor => ({
      isOver: monitor.isOver() && monitor.canDrop(),
      dropIndex: monitor.getItem()?.index
    }),
    drop: item => moveColumn(item.id, column.id)
  });

  const hasIconsAvailable = Boolean(columnMetaDataMap[dimensionTypeName]?.iconStyle);
  const hasThumbnailToggle =
    columnMetaDataMap[dimensionTypeName]?.iconStyle === 'thumbnail' &&
    dimensionTypeName === 'Ad Name';
  const thumbnailOn = icon !== null;
  // TVAD (linear/streaming) channels don't offer a Date Grouping picker — Date is always day-level there.
  const isTVAD = useMemo(() => [...STREAMING_PLUS, LINEAR].includes(prefix), [prefix]);

  const columnDropRef = useCallback(
    (node: HTMLDivElement | null) => {
      preview(node);
      drop(node);
    },
    [preview, drop]
  );
  const handleDragRef = useCallback(
    (node: HTMLDivElement | null) => {
      drag(node);
    },
    [drag]
  );

  const onDimensionChange = useCallback(
    <T extends keyof DimensionColumn>(i: number, key: T, value: DimensionColumn[T]) => {
      setPresetChanges(changes => {
        const dimensionColumnsUpdated = R.adjust(
          i,
          (col: DimensionColumn) => ({
            ...col,
            ...(key === 'dimensionTypeName' ? { icon: undefined } : {}),
            [key]: value
          }),
          changes.dimensionColumns
        );
        if (key === 'dimensionTypeName') {
          const metadata = columnMetaDataMap[value as string];
          dimensionColumnsUpdated[i] = {
            ...dimensionColumnsUpdated[i],
            dimensionVarName:
              !metadata && shouldEnableMetricsTableSegmentGroups
                ? (value as DimensionColumn['dimensionVarName'])
                : (metadata?.name as DimensionColumn['dimensionVarName']),
            label: undefined
          };
        }
        return { ...changes, dimensionColumns: dimensionColumnsUpdated };
      });
    },
    [setPresetChanges, columnMetaDataMap, shouldEnableMetricsTableSegmentGroups]
  );

  useEffect(() => {
    // Check to see if the column should have a logo/thumbnail by default. Check explicitly that the
    // value is undefined — null indicates the user directly chose "no icon".
    if (icon === undefined && hasIconsAvailable) {
      onDimensionChange(index, 'icon', 'hasIcon');
    }
    if (icon != null && !hasIconsAvailable) {
      onDimensionChange(index, 'icon', null as unknown as DimensionColumn['icon']);
    }
  }, [icon, hasIconsAvailable, onDimensionChange, index]);

  const typeOptions = useMemo(() => {
    const otherOptions = Object.entries(columnMetaDataMap).reduce<DropdownOption[]>(
      (options, [colPrettyName, colMetaData]) => {
        const filterOut = ['logo', 'icon', 'thumbnail'].some(term =>
          colPrettyName.toLowerCase().includes(term)
        );
        if (filterOut) return options;
        if (
          groupedSegments &&
          shouldEnableMetricsTableSegmentGroups &&
          Object.values(groupedSegments)
            .flatMap(group => Object.values(group))
            .some(segment => segment.segmentName === colPrettyName)
        ) {
          return options;
        }

        const dimVarName = colMetaData?.name ?? colPrettyName;
        const isInvalidAnalyticsDim =
          analyticsIsSource && isInvalidAnalyticsDimension(dimVarName, expAnalyticsCols);
        const isInvalidGranularityDim =
          Boolean(invalidGranularityDetails[dimVarName]) ||
          Boolean(incompatibleTableDimsFromFilter[dimVarName]);
        const disabled = isInvalidAnalyticsDim || isInvalidGranularityDim;
        const tooltipMessage = disabled
          ? makeInvalidDimMsg({
              prettyDims: [colPrettyName],
              isInvalidAnalyticsDim,
              conflictingTableDims: invalidGranularityDetails[dimVarName],
              conflictingFilterDims: incompatibleTableDimsFromFilter[dimVarName]
            })
          : undefined;

        return [
          ...options,
          {
            value: colPrettyName,
            label: colPrettyName,
            disabled,
            simpleTooltip: tooltipMessage,
            simpleTooltipVerticalShift: '-55px'
          }
        ];
      },
      []
    );

    if (groupedSegments && shouldEnableMetricsTableSegmentGroups) {
      Object.keys(groupedSegments).forEach(groupName => {
        otherOptions.push({ label: groupName, value: groupName });
      });
    }
    return otherOptions;
  }, [
    columnMetaDataMap,
    analyticsIsSource,
    groupedSegments,
    shouldEnableMetricsTableSegmentGroups,
    expAnalyticsCols,
    invalidGranularityDetails,
    incompatibleTableDimsFromFilter
  ]);

  return (
    <div className='column-config-outer' ref={columnDropRef}>
      <div
        className={cn('column-config', {
          'is-over': isOver,
          left: isOver && (dropIndex ?? -1) > index,
          right: isOver && (dropIndex ?? -1) <= index,
          dragging: isDragging,
          'is-hover-item': hoverItem === id
        })}
      >
        <ColumnHeaderTop
          noHeaders
          color={color}
          handleRef={handleDragRef}
          addColumn={addColumn}
          index={index}
        />
        <div className='basic-section'>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            label='Type'
            onChange={val => {
              if (
                shouldEnableMetricsTableSegmentGroups &&
                groupedSegments &&
                Object.keys(groupedSegments).includes(val)
              ) {
                onDimensionChange(index, 'dimensionTypeName', val);
                groupedSegments[val].forEach(segment => {
                  addColumn(dimensionColumns.length);
                  onDimensionChange(
                    dimensionColumns.length,
                    'dimensionTypeName',
                    segment.segmentName
                  );
                });
              } else {
                onDimensionChange(index, 'dimensionTypeName', val);
              }
            }}
            options={typeOptions}
            value={dimensionTypeName}
          />
        </div>
        <div className='form-section'>
          <div className='label'>Label</div>
          <OnBlurTextInput
            onChange={val => onDimensionChange(index, 'label', val || undefined)}
            placeholder={`${dimensionTypeName} (default)`}
            size='sm'
            value={label ?? ''}
          />
        </div>
        <div className='basic-section'>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            label='Icon'
            disabled={!hasIconsAvailable}
            onChange={val =>
              onDimensionChange(
                index,
                'icon',
                val === 'none'
                  ? (null as unknown as DimensionColumn['icon'])
                  : (val as DimensionColumn['icon'])
              )
            }
            value={icon ?? 'none'}
            options={[
              { label: 'None', value: 'none' },
              { label: 'Has Icon', value: 'hasIcon' },
              { label: 'Icon Only', value: 'iconOnly' }
            ]}
          />
        </div>
        {dimensionTypeName === 'Date' && !isTVAD && (
          <div className='basic-section'>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              label='Date Grouping'
              value={dateGrouping ?? 'day'}
              options={Object.keys(DATE_GROUPING_OPTIONS).map(key => ({
                label: DATE_GROUPING_OPTIONS[key],
                value: key
              }))}
              onChange={val =>
                onDimensionChange(index, 'dateGrouping', val as DimensionColumn['dateGrouping'])
              }
            />
          </div>
        )}
        <div className='basic-section'>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            label='Role'
            value={`${role ?? 10}`}
            options={Object.keys(roleMap).map(roleKey => ({
              label: roleMap[Number(roleKey)],
              value: roleKey
            }))}
            onChange={val => onDimensionChange(index, 'role', parseInt(val, 10))}
          />
        </div>
        {hasThumbnailToggle && (
          <div className='basic-section'>
            <ToggleSwitch
              label='Thumbnail'
              checked={thumbnailOn}
              onChange={() =>
                onDimensionChange(
                  index,
                  'icon',
                  thumbnailOn ? (null as unknown as DimensionColumn['icon']) : 'hasIcon'
                )
              }
            />
          </div>
        )}
        {!isOnlyColumn && (
          <Button
            appearance={Appearance.OUTLINED}
            design={Intent.ERROR}
            className='delete-button'
            onClick={() => deleteColumn(index)}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
};

type OnColumnChange = <T extends keyof Column>(i: number, key: T, value: Column[T]) => void;

interface ConfigDataColumnProps {
  addColumn: (i: number) => void;
  addCustomMetric: () => void;
  color?: string;
  column: Column;
  columnMetaDataMap: ColumnMetaDataMap;
  deleteColumn: DeleteColumn;
  deleteCustomMetric: (customMetric: CustomMetric) => void;
  duplicateCustomMetric: (customMetric: CustomMetric) => void;
  editCustomMetric: (customMetric: CustomMetric) => void;
  globalKpi: string;
  groupedColumnPicker?: GroupedColumnPicker;
  hasSuperHeader: boolean;
  hoverItem?: string;
  index: number;
  isOnlyColumn: boolean;
  kpiMetaData: Record<string, KpiInfo>;
  moveColumn: MoveColumn;
  onSuperHeaderAdd: () => void;
  prefix: SingleChannelMediaTypes | 'cross_channel' | 'pacing';
  renameCustomMetric: (customMetric: CustomMetric) => void;
  selectedCustomMetricActionId?: number;
  setSelectedCustomMetricActionId: (id: number | undefined) => void;
  setPresetChanges: Dispatch<SetStateAction<MetricsTablePreset>>;
  invalidGranularityDetails?: InvalidGranularityDetails;
  incompatibleTableDataColsFromFilter?: InvalidGranularityDetails;
}

const ConfigDataColumn = ({
  addColumn,
  addCustomMetric,
  color,
  column,
  columnMetaDataMap = {},
  deleteColumn,
  deleteCustomMetric,
  duplicateCustomMetric,
  editCustomMetric,
  globalKpi,
  groupedColumnPicker,
  hasSuperHeader,
  hoverItem,
  index,
  isOnlyColumn,
  kpiMetaData = {},
  moveColumn,
  onSuperHeaderAdd,
  prefix,
  renameCustomMetric,
  selectedCustomMetricActionId,
  setSelectedCustomMetricActionId,
  setPresetChanges,
  invalidGranularityDetails = {},
  incompatibleTableDataColsFromFilter = {}
}: ConfigDataColumnProps) => {
  const {
    audience,
    dataVarName: dataVarNameExtended,
    decimals,
    divider,
    heatMapping,
    id,
    kpi,
    label,
    lag,
    role,
    source
  } = column;
  const { currency_code: currencyCode } = useCompanyInfo();
  const dataVarName = useMemo(
    () => dataVarNameExtended.replace('Native', '').replace('_native', ''),
    [dataVarNameExtended]
  );

  const kpiOptions = useMemo(
    () => convertKPIMapToOptions(prefix, kpiMetaData),
    [kpiMetaData, prefix]
  );

  const onChange = useCallback<OnColumnChange>(
    (i, key, value) => {
      setPresetChanges(changes => ({
        ...changes,
        dataColumns: R.adjust(i, (col: Column) => ({ ...col, [key]: value }), changes.dataColumns)
      }));
    },
    [setPresetChanges]
  );

  const groupedSelection = useMemo(
    () => (groupedColumnPicker ? groupedColumnPicker.parse(dataVarNameExtended) : undefined),
    [groupedColumnPicker, dataVarNameExtended]
  );

  const resolveKpiDisplayName = useCallback(
    (kpiVal?: string): string => {
      const effective = kpiVal || globalKpi;
      return kpiOptions.find(opt => opt.value === effective)?.label ?? effective;
    },
    [kpiOptions, globalKpi]
  );

  const pickerSourceOptions = groupedColumnPicker?.sourceOptions;
  const resolveSourceDisplayName = useCallback(
    (sourceVal?: string): string | undefined =>
      sourceVal
        ? ((pickerSourceOptions ?? []).find(opt => opt.value === sourceVal)?.label ?? sourceVal)
        : undefined,
    [pickerSourceOptions]
  );

  const applyGroupedSelection = useCallback(
    (primary: string, secondary: string, kpiVal?: string, sourceVal?: string) => {
      if (!groupedColumnPicker) return;
      const showKpi = groupedColumnPicker.showKpiForPrimary(primary);
      const effectiveKpi = showKpi ? kpiVal : undefined;
      const showSource = groupedColumnPicker.showSourceForPrimary?.(primary) ?? false;
      const effectiveSource = showSource ? sourceVal : undefined;
      const newVar = groupedColumnPicker.toDataVarName(primary, secondary);
      onChange(index, 'dataVarName', newVar);
      onChange(index, 'kpi', effectiveKpi);
      onChange(index, 'source', effectiveSource);
      onChange(
        index,
        'label',
        groupedColumnPicker.headerLabel(
          primary,
          secondary,
          showKpi ? resolveKpiDisplayName(effectiveKpi) : undefined,
          showSource ? resolveSourceDisplayName(effectiveSource) : undefined
        )
      );
      const meta = columnMetaDataMap[newVar];
      if (meta?.decimals !== undefined) {
        onChange(index, 'decimals', meta.decimals);
      } else if (metricDecimalMap[newVar]) {
        onChange(index, 'decimals', metricDecimalMap[newVar]);
      } else {
        onChange(index, 'decimals', undefined);
      }
    },
    [
      groupedColumnPicker,
      index,
      onChange,
      columnMetaDataMap,
      resolveKpiDisplayName,
      resolveSourceDisplayName
    ]
  );

  const [isSpecial, isComputedCurrencyCol, metadata, category] = useMemo(() => {
    const baseMeta =
      columnMetaDataMap[dataVarName] ?? columnMetaDataMap[Object.keys(columnMetaDataMap)[0]];
    const basicColumnTypes = BASIC_COLUMN_TYPES_MAP[prefix] ?? DELIVERY_COLS_AND_METRICS;
    const special = !basicColumnTypes.includes(dataVarName);
    const computedCurrencyCol = (CC.COMPUTED_CURRENCY_DATA_TYPES as readonly string[]).includes(
      dataVarName
    );
    const kpiInfo = special ? kpiMetaData[kpi || globalKpi] : undefined;
    const meta =
      special && baseMeta && kpiInfo
        ? {
            ...baseMeta,
            decimals: kpiInfo.decimals,
            prettyName: baseMeta.prettyName?.replace('CPX', kpiInfo.cpxName)
          }
        : baseMeta;
    return [special, computedCurrencyCol, meta, meta?.category] as const;
  }, [columnMetaDataMap, dataVarName, prefix, kpiMetaData, kpi, globalKpi]);

  const [{ isDragging }, drag, preview] = useDrag<
    { type: typeof COLUMN_DND_TYPE; id: string; index: number },
    void,
    { isDragging: boolean }
  >({
    type: COLUMN_DND_TYPE,
    item: { type: COLUMN_DND_TYPE, id, index },
    collect: monitor => ({ isDragging: monitor.isDragging() })
  });

  const [{ isOver, dropIndex }, drop] = useDrop<
    { type: typeof COLUMN_DND_TYPE; id: string; index: number },
    void,
    { isOver: boolean; dropIndex: number | undefined }
  >({
    accept: COLUMN_DND_TYPE,
    canDrop: item => item.id !== id,
    collect: monitor => ({
      isOver: monitor.isOver() && monitor.canDrop(),
      dropIndex: monitor.getItem()?.index
    }),
    drop: item => moveColumn(item.id, column.id)
  });

  const columnDropRef = useCallback(
    (node: HTMLDivElement | null) => {
      preview(node);
      drop(node);
    },
    [preview, drop]
  );
  const handleDragRef = useCallback(
    (node: HTMLDivElement | null) => {
      drag(node);
    },
    [drag]
  );

  const defaultLabel = useMemo(() => {
    if (dataVarName.startsWith('avgRevenue')) {
      return `${kpiMetaData[kpi || globalKpi]?.revenueHeader || 'Avg. Rev.'} (default)`;
    }
    return `${metadata?.prettyName ?? dataVarName} (default)`;
  }, [dataVarName, metadata, kpiMetaData, kpi, globalKpi]);

  const [categorySelectVal, categorySelectOptions] = useMemo(() => {
    const categoryOptions = Object.values(StreamingColumnCategory).map(cat => ({
      label: cat,
      value: cat
    }));
    return [
      category ? { value: category, label: category } : null,
      [...categoryOptions].sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase())
      )
    ];
  }, [category]);

  const getValidTypeOptions = useCallback(
    (
      categoryOverride: string | null = null
    ): Record<string, DropdownOption & { label: string }> => {
      const columnMetaDataMapFiltered: ColumnMetaDataMap = {};
      for (const key of Object.keys(columnMetaDataMap)) {
        // Native metrics are selected via the currency-type dropdown instead.
        if ((CCM.NATIVE_CURRENCY_COLUMN_TYPES as readonly string[]).includes(key)) continue;
        const col = columnMetaDataMap[key] as ColumnMetaData;
        const categoryToFilterBy = categoryOverride ?? categorySelectVal?.value;
        if (!categoryToFilterBy || col.category === categoryToFilterBy) {
          columnMetaDataMapFiltered[key] = columnMetaDataMap[key];
        }
        if (key === 'impressions' || key === 'imps') {
          columnMetaDataMapFiltered[key] = {
            ...columnMetaDataMapFiltered[key],
            prettyName: 'Impressions'
          } as ColumnMetaData;
        }
      }
      const typeOptions: Record<string, DropdownOption & { label: string }> = {};
      for (const [type, val] of Object.entries(columnMetaDataMapFiltered)) {
        if (!val) continue;
        const peerBlockers = invalidGranularityDetails[type];
        const filterBlockers = incompatibleTableDataColsFromFilter[type];
        const isInvalidGranularity = Boolean(peerBlockers) || Boolean(filterBlockers);
        typeOptions[type] = {
          className: cn({
            'dropdown-open':
              val.customMetaData && selectedCustomMetricActionId === val.customMetaData.id
          }),
          label: val.prettyName || type,
          value: type,
          disabled: isInvalidGranularity,
          simpleTooltip: isInvalidGranularity
            ? makeInvalidDimMsg({
                prettyDims: [val.prettyName || type],
                isInvalidAnalyticsDim: false,
                conflictingTableDims: peerBlockers,
                conflictingFilterDims: filterBlockers
              })
            : undefined,
          simpleTooltipVerticalShift: '-55px',
          trailingContent: val.isCustom ? (
            <div className='custom-metric-label'>custom</div>
          ) : undefined,
          itemOptions: val.customMetaData
            ? renderCustomMetricActions(
                val.customMetaData,
                selectedCustomMetricActionId,
                setSelectedCustomMetricActionId,
                { deleteCustomMetric, duplicateCustomMetric, editCustomMetric, renameCustomMetric }
              )
            : undefined
        };
      }
      return typeOptions;
    },
    [
      categorySelectVal,
      columnMetaDataMap,
      deleteCustomMetric,
      duplicateCustomMetric,
      editCustomMetric,
      selectedCustomMetricActionId,
      setSelectedCustomMetricActionId,
      invalidGranularityDetails,
      incompatibleTableDataColsFromFilter,
      renameCustomMetric
    ]
  );

  const [typeSelectVal, typeSelectOptions] = useMemo(() => {
    const typeOptions = getValidTypeOptions();
    const selectedVar = typeOptions[dataVarName] ? dataVarName : Object.keys(typeOptions)[0];
    const sorted = Object.values(typeOptions).sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase())
    );
    return [{ value: selectedVar, label: typeOptions[selectedVar]?.label ?? selectedVar }, sorted];
  }, [getValidTypeOptions, dataVarName]);

  useEffect(() => {
    if (typeSelectVal.value && typeSelectVal.value !== dataVarName) {
      onChange(index, 'dataVarName', typeSelectVal.value);
    }
  }, [typeSelectVal, dataVarName, index, onChange]);

  const renderKpiSourcePicker = () => {
    if (groupedColumnPicker) {
      if (!groupedSelection) {
        return null;
      }
      return (
        <>
          {groupedColumnPicker.showKpiForPrimary(groupedSelection.primary) && (
            <div className='basic-section kpi-section'>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                label='KPI'
                onChange={val =>
                  applyGroupedSelection(
                    groupedSelection.primary,
                    groupedSelection.secondary,
                    val === 'Global' ? undefined : val,
                    source
                  )
                }
                options={[{ label: 'Global', value: 'Global' }, ...kpiOptions]}
                value={kpi ?? 'Global'}
              />
            </div>
          )}
          {(groupedColumnPicker.showSourceForPrimary?.(groupedSelection.primary) ?? false) && (
            <div className='basic-section kpi-section'>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                label='Source'
                onChange={val =>
                  applyGroupedSelection(
                    groupedSelection.primary,
                    groupedSelection.secondary,
                    kpi,
                    val === 'Global' ? undefined : val
                  )
                }
                options={[
                  { label: 'Global', value: 'Global' },
                  ...(groupedColumnPicker.sourceOptions ?? [])
                ]}
                value={source ?? 'Global'}
              />
            </div>
          )}
        </>
      );
    }
    if (isSpecial) {
      return (
        <>
          <div className='basic-section kpi-section'>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              label='KPI'
              onChange={val => onChange(index, 'kpi', val === 'Global' ? undefined : val)}
              options={[{ label: 'Global', value: 'Global' }, ...kpiOptions]}
              value={kpi ?? 'Global'}
            />
          </div>
          <div className='basic-section'>
            {!['linear', 'youtube'].includes(prefix) && (
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                label='Lag'
                onChange={val =>
                  onChange(index, 'lag', val === 'Global' ? undefined : (val as Column['lag']))
                }
                options={['Global', ...S.LAGS]}
                value={lag || 'Global'}
              />
            )}
            {prefix === 'linear' && (
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                label='Audience'
                onChange={val =>
                  onChange(
                    index,
                    'audience',
                    val === 'Global' ? undefined : (val as Column['audience'])
                  )
                }
                options={['Global', ...L.AUDIENCES]}
                value={audience || 'Global'}
              />
            )}
          </div>
        </>
      );
    }
    return null;
  };

  return (
    <div className='column-config-outer' ref={columnDropRef}>
      <div
        className={cn('column-config', {
          'has-divider': divider,
          'is-over': isOver,
          left: isOver && (dropIndex ?? -1) > index,
          right: isOver && (dropIndex ?? -1) <= index,
          dragging: isDragging,
          'is-hover-item': hoverItem === id
        })}
      >
        <ColumnHeaderTop
          addColumn={addColumn}
          color={color}
          handleRef={handleDragRef}
          hasSuperHeader={hasSuperHeader}
          onSuperHeaderAdd={onSuperHeaderAdd}
          index={index}
        />
        {category && (
          <div className='form-section'>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              label='Category'
              options={categorySelectOptions}
              value={categorySelectVal?.value}
              onChange={val => {
                const newTypeOptions = getValidTypeOptions(val);
                if (!Object.keys(newTypeOptions).includes(dataVarName)) {
                  onChange(index, 'dataVarName', Object.keys(newTypeOptions)[0]);
                }
              }}
            />
          </div>
        )}
        {groupedColumnPicker && groupedSelection ? (
          <>
            <div className='form-section'>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                label={groupedColumnPicker.primaryLabel}
                options={groupedColumnPicker.primaryOptions}
                value={groupedSelection.primary}
                onChange={val => {
                  // Clamp the secondary to the new primary's options — a ratio metric's reduced
                  // variant set may not contain the prior secondary.
                  const opts =
                    groupedColumnPicker.secondaryOptionsForPrimary?.(val) ??
                    groupedColumnPicker.secondaryOptions;
                  const secondary = opts.some(o => o.value === groupedSelection.secondary)
                    ? groupedSelection.secondary
                    : (opts[0]?.value ?? groupedSelection.secondary);
                  applyGroupedSelection(val, secondary, kpi, source);
                }}
              />
            </div>
            <div className='form-section'>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                label={groupedColumnPicker.secondaryLabel}
                options={
                  groupedColumnPicker.secondaryOptionsForPrimary?.(groupedSelection.primary) ??
                  groupedColumnPicker.secondaryOptions
                }
                value={groupedSelection.secondary}
                onChange={val => applyGroupedSelection(groupedSelection.primary, val, kpi, source)}
              />
            </div>
          </>
        ) : (
          <div className='form-section' data-testid={`column-type-section-${id}`}>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              className='column-type-dropdown'
              label='Type'
              options={typeSelectOptions}
              value={typeSelectVal.value}
              footer={
                <div
                  className='add-new-custom-metric-button'
                  data-testid='add-new-custom-metric-button'
                >
                  <MdAdd /> Add New Custom Metric
                </div>
              }
              footerOnClick={addCustomMetric}
              onChange={val => {
                onChange(index, 'dataVarName', val);
                onChange(index, 'label', undefined);
                const meta = columnMetaDataMap[val];
                if (meta?.isCustom) {
                  onChange(
                    index,
                    'requiredColumns',
                    meta.requiredColumns ? [...meta.requiredColumns] : undefined
                  );
                  onChange(index, 'customColumnID', meta.customMetaData?.id);
                } else {
                  const basicColumnTypes =
                    BASIC_COLUMN_TYPES_MAP[prefix] ?? DELIVERY_COLS_AND_METRICS;
                  if (basicColumnTypes.includes(val)) {
                    onChange(index, 'kpi', undefined);
                    onChange(index, 'lag', undefined);
                    onChange(index, 'audience', undefined);
                  }
                  onChange(index, 'requiredColumns', undefined);
                }
                if (meta?.decimals !== undefined) {
                  onChange(index, 'decimals', meta.decimals);
                } else if (metricDecimalMap[dataVarName]) {
                  onChange(index, 'decimals', metricDecimalMap[dataVarName]);
                } else {
                  onChange(index, 'decimals', undefined);
                }
              }}
            />
          </div>
        )}
        <div className='form-section'>
          <div className='label'>Label</div>
          <OnBlurTextInput
            size='sm'
            value={label ?? ''}
            placeholder={defaultLabel}
            onChange={val => onChange(index, 'label', val || undefined)}
          />
        </div>
        {renderKpiSourcePicker()}
        {typeSelectVal.value === 'impressions' || typeSelectVal.value === 'imps' ? (
          <div className='form-section'>
            <div className='label'>Format:</div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              value={(decimals ?? metadata?.decimals) === -1 ? 'raw' : 'thousands'}
              options={[
                { value: 'thousands', label: 'View in thousands' },
                { value: 'raw', label: 'View whole number ' }
              ]}
              onChange={val => {
                const labelToUse = val === 'thousands' ? 'Impressions (000s)' : 'Impressions';
                onChange(index, 'label', labelToUse);
                onChange(index, 'decimals', val === 'thousands' ? 0 : -1);
              }}
            />
          </div>
        ) : (
          <div className='form-section'>
            <div className='label'>Decimals:</div>
            <OnBlurTextInput
              size='sm'
              type='number'
              min='0'
              value={`${decimals ?? metadata?.decimals ?? ''}`}
              onChange={val => {
                const parsed = parseInt(val, 10);
                onChange(
                  index,
                  'decimals',
                  Number.isNaN(parsed) || parsed < 0 ? undefined : resolveDecimals(parsed)
                );
              }}
              placeholder={`${metadata?.decimals ?? metricDecimalMap[dataVarName] ?? 0} (default)`}
            />
          </div>
        )}
        {isComputedCurrencyCol && prefix !== 'commerce' && (
          <div className='form-section'>
            <div className='label currency-type'>
              <span>Currency Type:</span>
              <InfoTooltip size={Size.SM}>
                Select how currency values are displayed for this metric.
                <ul>
                  <li>
                    {`Normalized to ${currencyCode}`} (Default):{' '}
                    <span>{`Converts foreign currency values to ${currencyCode} using daily exchange rates.`}</span>
                  </li>
                  <li>
                    Raw:{' '}
                    <span>
                      Shows values as reported by the platform in their original currency.
                    </span>
                  </li>
                </ul>
                Important:{' '}
                <span>When we do not know a platform’s raw currency we assume it is USD.</span>
              </InfoTooltip>
            </div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              onChange={val => {
                const metric = CCM.NORMAL_NATIVE_METRIC_MAP[typeSelectVal.value]?.[val];
                onChange(index, 'dataVarName', metric as Column['dataVarName']);
                onChange(index, 'requiredColumns', undefined);
              }}
              options={[
                { label: `Normalized to ${currencyCode}`, value: 'normalized' },
                { label: 'Raw Value', value: 'native' }
              ]}
              value={
                (CCM.NATIVE_CURRENCY_COLUMN_TYPES as readonly string[]).includes(
                  dataVarNameExtended
                )
                  ? 'native'
                  : 'normalized'
              }
            />
          </div>
        )}
        {index > 0 && (
          <div className='basic-section checkbox-section'>
            <div className='label'>Divider?:</div>
            <Checkbox
              checked={Boolean(divider)}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChange(index, 'divider', e.target.checked)
              }
            />
          </div>
        )}
        <div className='basic-section'>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            label='Role'
            value={`${role ?? 10}`}
            options={Object.keys(roleMap).map(roleKey => ({
              label: roleMap[Number(roleKey)],
              value: roleKey
            }))}
            onChange={val => onChange(index, 'role', parseInt(val, 10))}
          />
        </div>
        <div className='basic-section checkbox-section'>
          <div className='label'>Heat Mapping?:</div>
          <Checkbox
            checked={Boolean(heatMapping)}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange(
                index,
                'heatMapping',
                e.target.checked ? { colorScheme: 'diverging' } : undefined
              )
            }
          />
        </div>
        {heatMapping && (
          <div className='heatmapping-section'>
            <div className='header-section'>
              <div className='label'>Heat Mapping</div>
              <OverlayTrigger
                trigger='click'
                placement={Placement.TOP}
                overlay={
                  <Popover
                    id={`${id}-heatmap-glossary-popover`}
                    className='performance-heatmap-glossary-popover'
                  >
                    <Popover.Header>Glossary</Popover.Header>
                    <Popover.Body>
                      <div className='glossary-item'>
                        <span>Exclude Zeros:</span>
                        <span>
                          Ignores zeros when calculating. They aren&apos;t factored into means,
                          medians, or std. dev. calculations, nor are they considered minimum or
                          maximum values.
                        </span>
                      </div>
                      <div className='glossary-item'>
                        <span>Pivot:</span>
                        <span>
                          Point at which colors change. Does not apply to &quot;Green&quot; color
                          scheme.
                        </span>
                      </div>
                      <div className='glossary-item'>
                        <span>Mid Point:</span>
                        <span>
                          Half way between the maximum and minimum values (respects Std. Dev.
                          Clipping).
                        </span>
                      </div>
                      <div className='glossary-item'>
                        <span>Color:</span>
                        <span>
                          Color scheme. &quot;Standard&quot; goes from red to yellow, then yellow to
                          green (yellow being at the pivot). &quot;Green&quot; goes from white to
                          green.
                        </span>
                      </div>
                      <div className='glossary-item'>
                        <span>Std. Dev. Clipping:</span>
                        <span>
                          Numbers that are greater or less than this number of standard deviations
                          from the mean (not pivot) will not be considered when heat mapping. This
                          is to prevent outliers from impacting the heat mapping.
                        </span>
                      </div>
                      <div className='glossary-item'>
                        <span>Minimum/Maximum:</span>
                        <span>
                          Like &quot;Std. Dev. Clipping&quot;, but hard-coded. Numbers below the
                          minimum and above the maximum are treated as if they are those numbers. If
                          you put a maximum of 100, a value of 500 is treated as 100 for heat
                          mapping calculations.
                        </span>
                      </div>
                    </Popover.Body>
                  </Popover>
                }
              >
                <span className='glossary'>(Glossary)</span>
              </OverlayTrigger>
            </div>
            {/* NOTE: bpm gated this checkbox on `metadata.contentReplacement?.threshold === 0`; that
                field doesn't exist on tinuiti's ColumnMetaData yet, so it's always shown here (a minor,
                flagged simplification). */}
            <div className='basic-section checkbox-section'>
              <div className='label'>Exclude Zeros</div>
              <Checkbox
                checked={Boolean(heatMapping.excludeZeros)}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onChange(index, 'heatMapping', { ...heatMapping, excludeZeros: e.target.checked })
                }
              />
            </div>
            <div className='basic-section'>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                label='Pivot'
                onChange={val => onChange(index, 'heatMapping', { ...heatMapping, midpoint: val })}
                options={['MEDIAN', 'MIDPOINT', 'MEAN', 'CUSTOM']}
                value={heatMapping.midpoint || 'MIDPOINT'}
              />
            </div>
            {heatMapping.midpoint === 'CUSTOM' && (
              <div className='form-section'>
                <div className='label'>Custom Pivot:</div>
                <OnBlurTextInput
                  size='sm'
                  type='number'
                  value={`${heatMapping.customMidpoint ?? ''}`}
                  onChange={val => {
                    const parsed = parseFloat(val);
                    onChange(index, 'heatMapping', {
                      ...R.omit(['customMidpoint'], heatMapping),
                      customMidpoint: Number.isNaN(parsed) ? undefined : parsed
                    });
                  }}
                  placeholder='Custom pivot'
                />
              </div>
            )}
            <div className='basic-section'>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                label='Color'
                value={heatMapping.colorScheme || 'diverging'}
                options={[
                  { label: 'Diverging', value: 'diverging' },
                  { label: 'Sequential', value: 'sequential' }
                ]}
                onChange={val =>
                  onChange(index, 'heatMapping', { ...heatMapping, colorScheme: val })
                }
              />
            </div>
            <div className='basic-section checkbox-section'>
              <div className='label'>Min Is Best?:</div>
              <Checkbox
                checked={resolveMinIsBest(dataVarName, heatMapping, metadata)}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onChange(index, 'heatMapping', {
                    ...R.omit(['minIsBest'], heatMapping),
                    minIsBest: e.target.checked
                  })
                }
              />
            </div>
            <div className='form-section'>
              <div className='label'>Std. Dev. Clipping:</div>
              <OnBlurTextInput
                size='sm'
                type='number'
                value={`${heatMapping.stdevClipping ?? ''}`}
                onChange={val => {
                  const parsed = parseFloat(val);
                  onChange(index, 'heatMapping', {
                    ...R.omit(['stdevClipping'], heatMapping),
                    stdevClipping: Number.isNaN(parsed) ? undefined : parsed
                  });
                }}
                placeholder='None (default)'
              />
            </div>
            <div className='form-section'>
              <div className='label'>Minimum:</div>
              <OnBlurTextInput
                size='sm'
                type='number'
                value={`${heatMapping.min ?? ''}`}
                onChange={val => {
                  const parsed = parseFloat(val);
                  onChange(index, 'heatMapping', {
                    ...R.omit(['min'], heatMapping),
                    min: Number.isNaN(parsed) ? undefined : parsed
                  });
                }}
                placeholder='None (default)'
              />
            </div>
            <div className='form-section'>
              <div className='label'>Maximum:</div>
              <OnBlurTextInput
                size='sm'
                type='number'
                value={`${heatMapping.max ?? ''}`}
                onChange={val => {
                  const parsed = parseFloat(val);
                  onChange(index, 'heatMapping', {
                    ...R.omit(['max'], heatMapping),
                    max: Number.isNaN(parsed) ? undefined : parsed
                  });
                }}
                placeholder='None (default)'
              />
            </div>
          </div>
        )}
        {!isOnlyColumn && (
          <Button
            appearance={Appearance.OUTLINED}
            design={Intent.ERROR}
            className='delete-button'
            onClick={() => deleteColumn(index)}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
};
